import {
  buildOrderPartyIdentityPayload,
  identityFromSelection,
  strPartyId,
  type PartyIdentity,
} from "./orderPartyIdentity";
import { resolveSupplierOrganizationId } from "../api/salesOrderApi";

export type InstallationPartyIdentityPayload = {
  seller_org_id?: string;
  seller_membership_id?: string;
  seller_global_user_id?: string;
  buyer_org_id?: string;
  buyer_membership_id?: string;
  buyer_global_user_id?: string;
  source_org_id?: string;
  source_membership_id?: string;
  source_global_user_id?: string;
  brokerage_firm_org_id?: string | null;
  team_member_org_id?: string | null;
  team_member_membership_id?: string | null;
  team_member_global_user_id?: string | null;
  agent_org_id?: string | null;
  agent_membership_id?: string | null;
  agent_global_user_id?: string | null;
};

export type InstallationSessionActor = {
  membershipId?: string;
  globalUserId?: string;
  organizationId?: string;
  licenseType?: string;
  isTeammember?: boolean;
  parentBrokerageOrgId?: string | null;
};

export type ResolveInstallationPartyInput = {
  selectedOfficeAgent?: Record<string, unknown> | null;
  selectedAgent?: Record<string, unknown> | null;
  selectedOffice?: { value?: string; label?: string; organizationId?: string; organization_id?: string } | null;
  selectedBrokerageFirm?: Record<string, unknown> | null;
  selectedTeamMemberOption?: Record<string, unknown> | null;
  selectedTeamMember?: Record<string, unknown> | null;
  teamMemberFromContinue?: Record<string, unknown> | null;
  valuesTeamMemberId?: string;
  valuesTeamMemberName?: string;
  offices?: Array<{ _id?: string; officeAddress?: unknown[] }> | null;
  savedOrder?: Record<string, unknown> | null;
  /** Logged-in actor — fills team member / buyer org when UI selection omits foundation ids. */
  sessionActor?: InstallationSessionActor | null;
};

export type ResolvedInstallationPartyContext = {
  inventorySource: string;
  sourceId: string | null;
  customerId: string | null;
  agentId: string | null;
  agentName: string | null;
  teamMemberId: string | null;
  teamMemberName: string | null;
  brokerageFirmId: string | null;
  brokerageFirmName: string | null;
  officeInfo: unknown;
  buyer: PartyIdentity;
  source: PartyIdentity;
  agent: PartyIdentity | null;
  teamMember: PartyIdentity | null;
  brokerageFirmOrgId: string | null;
};

function officeInfoFromSelection(
  selectedOffice: { value?: string; label?: string } | null | undefined,
  offices?: Array<{ _id?: string; officeAddress?: unknown[] }> | null
) {
  if (!selectedOffice?.value) return null;
  const selectedOfficeData = offices?.find((office) => office._id === selectedOffice.value);
  const primaryAddress = selectedOfficeData?.officeAddress?.[0];
  return {
    _id: selectedOffice.value,
    officeName: selectedOffice.label,
    officeType: selectedOffice.label,
    officeAddress: primaryAddress,
    status: true,
  };
}

/** Resolve buyer/agent/team/source legacy ids + foundation identities from installation form selections. */
export function resolveInstallationPartyContext(
  input: ResolveInstallationPartyInput
): ResolvedInstallationPartyContext {
  const saved = input.savedOrder || {};
  const session = input.sessionActor;
  const isSessionTeammember =
    !!session?.isTeammember ||
    String(session?.licenseType || "").toUpperCase() === "BROKERAGE_TEAM_MEMBER";
  let inventorySource = "agent";
  let sourceId: string | null = null;
  let customerId: string | null = null;
  let agentId: string | null = null;
  let agentName: string | null = null;
  let officeInfo: unknown = null;
  let brokerageFirmId: string | null = strPartyId(
    input.selectedBrokerageFirm?.value ?? input.selectedBrokerageFirm?.id
  ) || null;
  let brokerageFirmName: string | null = String(input.selectedBrokerageFirm?.label ?? "").trim() || null;

  let effectiveTeamMemberId = strPartyId(
    input.valuesTeamMemberId ??
      input.selectedTeamMemberOption?.value ??
      input.selectedTeamMemberOption?.membershipId ??
      input.selectedTeamMember?.value ??
      input.teamMemberFromContinue?.value ??
      saved.team_member_id ??
      saved.team_member_membership_id
  );
  if (!effectiveTeamMemberId && isSessionTeammember) {
    effectiveTeamMemberId = strPartyId(session?.membershipId);
  }
  let effectiveTeamMemberName = String(
    input.valuesTeamMemberName ??
      input.selectedTeamMemberOption?.label ??
      input.selectedTeamMember?.label ??
      input.teamMemberFromContinue?.label ??
      saved.team_member_name ??
      ""
  ).trim();
  if (!effectiveTeamMemberName && isSessionTeammember) {
    effectiveTeamMemberName = String(input.valuesTeamMemberName ?? saved.team_member_name ?? "").trim();
  }

  const teamMember = effectiveTeamMemberId
    ? identityFromSelection(
        (input.selectedTeamMemberOption ??
          input.selectedTeamMember ??
          input.teamMemberFromContinue ?? {
            membershipId: effectiveTeamMemberId,
            value: effectiveTeamMemberId,
            globalUserId:
              session?.globalUserId ?? saved.team_member_global_user_id,
            organizationId:
              session?.organizationId ??
              session?.parentBrokerageOrgId ??
              saved.team_member_org_id,
          }) as Record<string, unknown>
      )
    : isSessionTeammember && session?.membershipId
      ? identityFromSelection({
          membershipId: session.membershipId,
          globalUserId: session.globalUserId,
          organizationId: session.organizationId ?? session.parentBrokerageOrgId,
        })
      : identityFromSelection({
          membershipId: saved.team_member_membership_id ?? saved.team_member_id,
          globalUserId: saved.team_member_global_user_id,
          organizationId: saved.team_member_org_id,
        });

  if (input.selectedOfficeAgent) {
    const agent = identityFromSelection(input.selectedOfficeAgent);
    agentId = agent.membershipId || strPartyId(input.selectedOfficeAgent.value);
    sourceId = agentId;
    agentName = String(input.selectedOfficeAgent.label ?? "").trim() || null;
    customerId = agentId;
    inventorySource = "agent";
    officeInfo = officeInfoFromSelection(input.selectedOffice, input.offices);
    if (input.selectedAgent?.brokerageFirmId) {
      brokerageFirmId = strPartyId(input.selectedAgent.brokerageFirmId) || brokerageFirmId;
      brokerageFirmName =
        String(input.selectedAgent.brokerageFirmName ?? "").trim() || brokerageFirmName;
    }
  } else if (input.selectedAgent) {
    const agent = identityFromSelection(input.selectedAgent);
    agentId = agent.membershipId || strPartyId(input.selectedAgent.value);
    sourceId = agentId;
    agentName = String(input.selectedAgent.label ?? "").trim() || null;
    customerId = agentId;
    inventorySource = "agent";
    officeInfo = null;
    if (input.selectedAgent.brokerageFirmId) {
      brokerageFirmId = strPartyId(input.selectedAgent.brokerageFirmId) || brokerageFirmId;
      brokerageFirmName =
        String(input.selectedAgent.brokerageFirmName ?? "").trim() || brokerageFirmName;
    }
  } else if (input.selectedOffice) {
    officeInfo = officeInfoFromSelection(input.selectedOffice, input.offices);
    sourceId = strPartyId(input.selectedOffice.value) || null;
    inventorySource = "office";
    agentId = null;
    agentName = null;
    customerId =
      effectiveTeamMemberId ||
      strPartyId(input.selectedOffice?.value) ||
      strPartyId(input.selectedBrokerageFirm?.value) ||
      strPartyId(saved.customer_id) ||
      null;
  } else if (input.selectedBrokerageFirm) {
    sourceId = strPartyId(input.selectedBrokerageFirm.value) || null;
    inventorySource = "office";
    customerId = effectiveTeamMemberId || sourceId;
  } else if (saved && Object.keys(saved).length > 0) {
    sourceId = strPartyId(
      saved.source_id ??
        saved.source_membership_id ??
        saved.agent_id ??
        (saved.office as { _id?: string } | undefined)?._id
    );
    customerId = strPartyId(
      saved.customer_id ?? saved.buyer_membership_id ?? saved.agent_id ?? effectiveTeamMemberId
    );
    agentId = strPartyId(saved.agent_id ?? saved.agent_membership_id) || null;
    agentName = String(saved.agent_name ?? "").trim() || null;
    inventorySource = String(saved.inventory_source ?? (agentId ? "agent" : "office"));
    officeInfo = saved.office ?? null;
    brokerageFirmId = strPartyId(saved.brokerage_firm_id ?? saved.brokerage_firm_org_id) || null;
    brokerageFirmName = String(saved.brokerage_firm_name ?? "").trim() || null;
  }

  if (input.selectedOfficeAgent?.value) {
    customerId = strPartyId(input.selectedOfficeAgent.value) || customerId;
  } else if (input.selectedAgent?.value) {
    customerId = strPartyId(input.selectedAgent.value) || customerId;
  } else if (effectiveTeamMemberId) {
    customerId = effectiveTeamMemberId;
  } else if (!customerId && input.selectedBrokerageFirm?.value) {
    customerId = strPartyId(input.selectedBrokerageFirm.value) || null;
  }

  const agentParty = agentId
    ? input.selectedOfficeAgent
      ? identityFromSelection(input.selectedOfficeAgent)
      : input.selectedAgent
        ? identityFromSelection(input.selectedAgent)
        : identityFromSelection({
            membershipId: agentId,
            globalUserId: saved.agent_global_user_id,
            organizationId: saved.agent_org_id,
          })
    : identityFromSelection({
        membershipId: saved.agent_membership_id ?? saved.agent_id,
        globalUserId: saved.agent_global_user_id,
        organizationId: saved.agent_org_id,
      });

  const buyerMembershipId = strPartyId(
    customerId ?? agentId ?? effectiveTeamMemberId ?? sourceId
  );
  const buyer: PartyIdentity = {
    membershipId: buyerMembershipId,
    globalUserId:
      agentParty.globalUserId ||
      teamMember.globalUserId ||
      strPartyId(saved.buyer_global_user_id ?? saved.customer_global_user_id),
    organizationId:
      agentParty.organizationId ||
      teamMember.organizationId ||
      strPartyId(saved.buyer_org_id),
  };

  const source: PartyIdentity =
    inventorySource === "office"
      ? {
          // Office resource id lives on `office`; source membership is the team member when present.
          membershipId: strPartyId(teamMember.membershipId || effectiveTeamMemberId),
          organizationId: strPartyId(
            input.selectedBrokerageFirm?.organizationId ??
              input.selectedBrokerageFirm?.organization_id ??
              input.selectedBrokerageFirm?.value ??
              saved.brokerage_firm_org_id ??
              saved.brokerage_firm_id ??
              input.selectedOffice?.organizationId ??
              input.selectedOffice?.organization_id ??
              session?.parentBrokerageOrgId ??
              session?.organizationId
          ),
          globalUserId: teamMember.globalUserId || buyer.globalUserId,
        }
      : {
          membershipId: strPartyId(sourceId ?? agentId),
          organizationId: agentParty.organizationId || buyer.organizationId,
          globalUserId: agentParty.globalUserId || buyer.globalUserId,
        };

  const brokerageFirmOrgId = strPartyId(
    input.selectedBrokerageFirm?.organizationId ??
      input.selectedBrokerageFirm?.organization_id ??
      input.selectedAgent?.brokerageFirmOrgId ??
      input.selectedAgent?.brokerage_firm_organization_id ??
      session?.parentBrokerageOrgId ??
      session?.organizationId ??
      (inventorySource === "office" ? input.selectedBrokerageFirm?.value : "") ??
      saved.brokerage_firm_org_id ??
      saved.brokerage_firm_id
  ) || null;

  const buyerResolved: PartyIdentity = {
    ...buyer,
    organizationId: strPartyId(
      buyer.organizationId ||
        brokerageFirmOrgId ||
        session?.organizationId ||
        session?.parentBrokerageOrgId
    ),
  };

  const sourceResolved: PartyIdentity =
    inventorySource === "office"
      ? {
          ...source,
          organizationId: strPartyId(source.organizationId || brokerageFirmOrgId),
          globalUserId: source.globalUserId || teamMember.globalUserId || buyerResolved.globalUserId,
        }
      : source;

  return {
    inventorySource,
    sourceId,
    customerId,
    agentId,
    agentName,
    teamMemberId: effectiveTeamMemberId || null,
    teamMemberName: effectiveTeamMemberName || null,
    brokerageFirmId,
    brokerageFirmName,
    officeInfo,
    buyer: buyerResolved,
    source: sourceResolved,
    agent: agentParty.membershipId ? agentParty : null,
    teamMember: teamMember.membershipId ? teamMember : null,
    brokerageFirmOrgId,
  };
}

export function buildInstallationPartyIdentityPayload(args: {
  seller: PartyIdentity;
  party: ResolvedInstallationPartyContext;
  signageId?: string;
}): InstallationPartyIdentityPayload {
  const sellerOrgId =
    resolveSupplierOrganizationId(args.signageId) ||
    strPartyId(args.seller.organizationId);
  const seller: PartyIdentity = {
    membershipId: args.seller.membershipId,
    globalUserId: args.seller.globalUserId,
    organizationId: sellerOrgId || args.seller.organizationId,
  };

  const orderParty = buildOrderPartyIdentityPayload({
    seller,
    buyer: args.party.buyer,
    agent: args.party.agent,
    teamMember: args.party.teamMember,
    brokerageFirmOrgId: args.party.brokerageFirmOrgId,
  });

  const payload: InstallationPartyIdentityPayload = {
    seller_org_id: sellerOrgId || orderParty.seller_org_id,
    ...(orderParty.seller_membership_id
      ? { seller_membership_id: orderParty.seller_membership_id }
      : {}),
    ...(orderParty.seller_global_user_id
      ? { seller_global_user_id: orderParty.seller_global_user_id }
      : {}),
    buyer_membership_id: orderParty.buyer_membership_id,
    ...((orderParty.buyer_org_id || args.party.brokerageFirmOrgId)
      ? { buyer_org_id: orderParty.buyer_org_id || args.party.brokerageFirmOrgId || undefined }
      : {}),
    ...(orderParty.buyer_global_user_id
      ? {
          buyer_global_user_id: orderParty.buyer_global_user_id,
        }
      : {}),
    ...(args.party.source.organizationId
      ? { source_org_id: args.party.source.organizationId }
      : {}),
    ...(args.party.source.membershipId
      ? { source_membership_id: args.party.source.membershipId }
      : {}),
    ...(args.party.source.globalUserId
      ? { source_global_user_id: args.party.source.globalUserId }
      : {}),
    brokerage_firm_org_id: orderParty.brokerage_firm_org_id ?? args.party.brokerageFirmOrgId,
    ...(orderParty.team_member_org_id
      ? { team_member_org_id: orderParty.team_member_org_id }
      : {}),
    ...(orderParty.team_member_membership_id
      ? { team_member_membership_id: orderParty.team_member_membership_id }
      : {}),
    ...(orderParty.team_member_global_user_id
      ? { team_member_global_user_id: orderParty.team_member_global_user_id }
      : {}),
    ...(orderParty.agent_org_id ? { agent_org_id: orderParty.agent_org_id } : {}),
    ...(orderParty.agent_membership_id
      ? { agent_membership_id: orderParty.agent_membership_id }
      : {}),
    ...(orderParty.agent_global_user_id
      ? { agent_global_user_id: orderParty.agent_global_user_id }
      : {}),
  };

  return payload;
}

/** Rehydrate membership fields on PATCH when party UI is read-only. */
export function installationPartyIdentityFromSavedOrder(
  order: Record<string, unknown> | null | undefined
): InstallationPartyIdentityPayload {
  const o = order || {};
  const payload: InstallationPartyIdentityPayload = {};
  const sellerOrg = strPartyId(o.seller_org_id ?? o.signage_id);
  const sellerMembership = strPartyId(o.seller_membership_id);
  const sellerGlobal = strPartyId(o.seller_global_user_id);
  const buyerMembership = strPartyId(o.buyer_membership_id ?? o.customer_id);
  const buyerOrg = strPartyId(o.buyer_org_id);
  const buyerGlobal = strPartyId(o.buyer_global_user_id ?? o.customer_global_user_id);
  const sourceOrg = strPartyId(o.source_org_id ?? o.buyer_org_id ?? o.brokerage_firm_org_id);
  const sourceMembership = strPartyId(
    o.source_membership_id ??
      (String(o.inventory_source || "").toLowerCase() === "office"
        ? o.team_member_membership_id ?? o.team_member_id ?? o.buyer_membership_id
        : o.source_id ?? o.agent_membership_id ?? o.agent_id)
  );
  const sourceGlobal = strPartyId(o.source_global_user_id);

  if (sellerOrg) payload.seller_org_id = sellerOrg;
  if (sellerMembership) payload.seller_membership_id = sellerMembership;
  if (sellerGlobal) payload.seller_global_user_id = sellerGlobal;
  if (buyerOrg) payload.buyer_org_id = buyerOrg;
  if (buyerMembership) payload.buyer_membership_id = buyerMembership;
  if (buyerGlobal) payload.buyer_global_user_id = buyerGlobal;
  if (sourceOrg) payload.source_org_id = sourceOrg;
  if (sourceMembership) payload.source_membership_id = sourceMembership;
  if (sourceGlobal) payload.source_global_user_id = sourceGlobal;

  const brokerageOrg = strPartyId(o.brokerage_firm_org_id ?? o.brokerage_firm_id);
  if (brokerageOrg) payload.brokerage_firm_org_id = brokerageOrg;

  const teamOrg = strPartyId(o.team_member_org_id);
  const teamMembership = strPartyId(o.team_member_membership_id ?? o.team_member_id);
  const teamGlobal = strPartyId(o.team_member_global_user_id);
  if (teamOrg) payload.team_member_org_id = teamOrg;
  if (teamMembership) payload.team_member_membership_id = teamMembership;
  if (teamGlobal) payload.team_member_global_user_id = teamGlobal;

  const agentOrg = strPartyId(o.agent_org_id);
  const agentMembership = strPartyId(o.agent_membership_id ?? o.agent_id);
  const agentGlobal = strPartyId(o.agent_global_user_id);
  if (agentOrg) payload.agent_org_id = agentOrg;
  if (agentMembership) payload.agent_membership_id = agentMembership;
  if (agentGlobal) payload.agent_global_user_id = agentGlobal;

  return payload;
}

/** Agent / independent buyer placing their own installation order. */
export function resolveSelfBuyerInstallationPartyContext(args: {
  membershipId: string;
  globalUserId: string;
  organizationId?: string;
  userId: string;
  userName: string;
  teamMemberId?: string;
  teamMemberName?: string;
  teamMemberGlobalUserId?: string;
  teamMemberOrgId?: string;
}): ResolvedInstallationPartyContext {
  const buyerMembership = strPartyId(args.membershipId || args.userId);
  const buyer: PartyIdentity = {
    membershipId: buyerMembership,
    globalUserId: strPartyId(args.globalUserId),
    organizationId: strPartyId(args.organizationId),
  };
  const teamMemberMembership = strPartyId(args.teamMemberId);
  const teamMember: PartyIdentity | null = teamMemberMembership
    ? {
        membershipId: teamMemberMembership,
        globalUserId: strPartyId(args.teamMemberGlobalUserId),
        organizationId: strPartyId(args.teamMemberOrgId ?? args.organizationId),
      }
    : null;

  return {
    inventorySource: "agent",
    sourceId: buyerMembership,
    customerId: buyerMembership,
    agentId: buyerMembership,
    agentName: args.userName,
    teamMemberId: teamMemberMembership || null,
    teamMemberName: String(args.teamMemberName ?? "").trim() || null,
    brokerageFirmId: null,
    brokerageFirmName: null,
    officeInfo: null,
    buyer,
    source: buyer,
    agent: buyer,
    teamMember: teamMemberMembership ? teamMember : null,
    brokerageFirmOrgId: strPartyId(args.organizationId) || null,
  };
}

/** Session actor ids for installation party resolution (agent / team member / realtor). */
export function pickInstallationSessionActor(args: {
  membershipId?: string | null;
  globalUserId?: string | null;
  organizationId?: string | null;
  licenseType?: string | null;
  isTeammember?: boolean;
  parentBrokerageOrgId?: string | null;
}): InstallationSessionActor {
  return {
    membershipId: strPartyId(args.membershipId),
    globalUserId: strPartyId(args.globalUserId),
    organizationId: strPartyId(args.organizationId),
    licenseType: String(args.licenseType || "").trim(),
    isTeammember: !!args.isTeammember,
    parentBrokerageOrgId: strPartyId(args.parentBrokerageOrgId) || null,
  };
}

/** Legacy order-root keys removed from InstallationOrder / UpdateInput. */
const LEGACY_INSTALLATION_ORDER_KEYS = [
  "source_id",
  "brokerage_firm_id",
  "team_member_id",
  "agent_id",
  "customer_id",
  "signage_id",
  "customer_global_user_id",
  "customer_first_name",
  "customer_last_name",
  "customer_email",
  "customer_phone",
  "tax_amount",
  "product_discount_amount",
  "discount_applies_to",
  "special_fee_amount",
  "special_fee_tax_amount",
  "special_fee_tax_percentage",
] as const;

/** Legacy line-item keys removed from LineItem. */
const LEGACY_LINE_ITEM_KEYS = [
  "warehouse_id",
  "serial_ids",
  "discount",
  "unit_retail_price",
  "unit_purchase_price",
  "manual_unit_price_override",
  "product_location_status",
  "is_accessory",
] as const;

/**
 * Strip legacy fields so POST/PATCH bodies match installation_order.go.
 * Also maps installation_details.additional_instructions → installation_notes
 * and legacy installer_id/assignee_id → membership ids.
 */
export function sanitizeInstallationOrderSubmitPayload(
  payload: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...payload };
  for (const key of LEGACY_INSTALLATION_ORDER_KEYS) {
    delete out[key];
  }

  if (Array.isArray(out.line_items)) {
    out.line_items = (out.line_items as Record<string, unknown>[]).map((li) => {
      const cleaned = { ...li };
      if (cleaned.is_accessories == null && cleaned.is_accessory != null) {
        cleaned.is_accessories = !!cleaned.is_accessory;
      }
      for (const key of LEGACY_LINE_ITEM_KEYS) {
        delete cleaned[key];
      }
      return cleaned;
    });
  }

  const details = out.installation_details;
  if (details && typeof details === "object" && !Array.isArray(details)) {
    const d = { ...(details as Record<string, unknown>) };
    if (d.additional_instructions != null && d.installation_notes == null) {
      d.installation_notes = d.additional_instructions;
    }
    delete d.additional_instructions;

    // Legacy local-user ids → foundation membership ids (not org ids).
    if (d.installer_membership_id == null && d.installer_id != null) {
      d.installer_membership_id = d.installer_id;
    }
    if (d.assignee_membership_id == null && d.assignee_id != null) {
      d.assignee_membership_id = d.assignee_id;
    }
    delete d.installer_id;
    delete d.assignee_id;

    // Keep installer_* and assignee_* in sync (same foundation ids).
    if (d.assignee_membership_id == null && d.installer_membership_id != null) {
      d.assignee_membership_id = d.installer_membership_id;
    }
    if (d.assignee_org_id == null && d.installer_org_id != null) {
      d.assignee_org_id = d.installer_org_id;
    }
    if (d.assignee_global_user_id == null && d.installer_global_user_id != null) {
      d.assignee_global_user_id = d.installer_global_user_id;
    }
    if (d.installer_membership_id == null && d.assignee_membership_id != null) {
      d.installer_membership_id = d.assignee_membership_id;
    }
    if (d.installer_org_id == null && d.assignee_org_id != null) {
      d.installer_org_id = d.assignee_org_id;
    }
    if (d.installer_global_user_id == null && d.assignee_global_user_id != null) {
      d.installer_global_user_id = d.assignee_global_user_id;
    }

    out.installation_details = d;
  }

  return out;
}

export type InstallationDetailsParticipantSelection = {
  value?: string | null;
  label?: string | null;
  phone?: string | null;
  membershipId?: string | null;
  membership_id?: string | null;
  globalUserId?: string | null;
  global_user_id?: string | null;
  organizationId?: string | null;
  organization_id?: string | null;
};

export type InstallationDetailsParticipantFields = {
  installer_org_id?: string | null;
  installer_membership_id?: string | null;
  installer_global_user_id?: string | null;
  installer_name?: string | null;
  contact_number?: string | null;
  assignee_org_id?: string | null;
  assignee_membership_id?: string | null;
  assignee_global_user_id?: string | null;
};

/**
 * Build InstallationDetails installer + assignee foundation fields.
 * - signage_assignee: Assigned To person (value = membership id)
 * - vendor: marketplace seller for Brokerage / Brokerage TM / Brokerage Agent / Agent
 *   (value = organization id; membership/global from selected vendor profile)
 *
 * Selected vendor ids always go into installer_*; assignee_* mirrors the same ids.
 */
export function buildInstallationDetailsParticipantFields(args: {
  selection?: InstallationDetailsParticipantSelection | null;
  /** signage_assignee = person membership; vendor = seller org */
  role: "signage_assignee" | "vendor";
  fallbackOrgId?: string | null;
  fallbackMembershipId?: string | null;
  fallbackGlobalUserId?: string | null;
  fallbackName?: string | null;
  fallbackPhone?: string | null;
  /** When true (default), mirror installer ids onto assignee_* */
  mirrorAssignee?: boolean;
}): InstallationDetailsParticipantFields {
  const sel = args.selection || {};
  const value = strPartyId(sel.value);
  const explicitMembership = strPartyId(sel.membershipId ?? sel.membership_id);
  const explicitOrg = strPartyId(sel.organizationId ?? sel.organization_id);
  const explicitGlobal = strPartyId(sel.globalUserId ?? sel.global_user_id);

  let membershipId = explicitMembership || strPartyId(args.fallbackMembershipId);
  let organizationId = explicitOrg || strPartyId(args.fallbackOrgId);
  let globalUserId = explicitGlobal || strPartyId(args.fallbackGlobalUserId) || null;

  if (args.role === "signage_assignee") {
    if (!membershipId) membershipId = value;
    if (!organizationId) organizationId = strPartyId(args.fallbackOrgId);
  } else {
    // Brokerage / Agent Select Vendor → installer_* from selected vendor
    if (!organizationId) organizationId = value;
    if (!membershipId) membershipId = explicitMembership || strPartyId(args.fallbackMembershipId);
    if (!globalUserId) globalUserId = explicitGlobal || strPartyId(args.fallbackGlobalUserId) || null;
  }

  const name = String(sel.label ?? args.fallbackName ?? "").trim() || null;
  const phone = String(sel.phone ?? args.fallbackPhone ?? "").trim() || null;

  const fields: InstallationDetailsParticipantFields = {
    ...(organizationId ? { installer_org_id: organizationId } : {}),
    ...(membershipId ? { installer_membership_id: membershipId } : {}),
    ...(globalUserId ? { installer_global_user_id: globalUserId } : {}),
    ...(name ? { installer_name: name } : {}),
    contact_number: phone,
  };

  if (args.mirrorAssignee !== false) {
    if (organizationId) fields.assignee_org_id = organizationId;
    if (membershipId) fields.assignee_membership_id = membershipId;
    if (globalUserId) fields.assignee_global_user_id = globalUserId;
  }

  return fields;
}

/**
 * Build installation_details for create/update bodies (membership foundation only).
 * Backend no longer accepts installer_id / assignee_id.
 */
export function buildInstallationDetailsSubmitBlock(args: {
  scheduledDate: string | Date;
  installationAddress: unknown;
  selection?: InstallationDetailsParticipantSelection | null;
  role: "signage_assignee" | "vendor";
  fallbackOrgId?: string | null;
  fallbackMembershipId?: string | null;
  fallbackGlobalUserId?: string | null;
  fallbackName?: string | null;
  fallbackPhone?: string | null;
  installationNotes?: string | null;
  estimatedDuration?: unknown;
  actualInstallationDate?: unknown;
  taxDetails?: unknown;
  mirrorAssignee?: boolean;
}): Record<string, unknown> {
  const scheduled =
    args.scheduledDate instanceof Date
      ? args.scheduledDate.toISOString()
      : new Date(args.scheduledDate).toISOString();

  return {
    scheduled_date: scheduled,
    installation_address: args.installationAddress,
    ...buildInstallationDetailsParticipantFields({
      selection: args.selection,
      role: args.role,
      fallbackOrgId: args.fallbackOrgId,
      fallbackMembershipId: args.fallbackMembershipId,
      fallbackGlobalUserId: args.fallbackGlobalUserId,
      fallbackName: args.fallbackName,
      fallbackPhone: args.fallbackPhone,
      mirrorAssignee: args.mirrorAssignee,
    }),
    estimated_duration: args.estimatedDuration ?? null,
    actual_installation_date: args.actualInstallationDate ?? null,
    installation_notes: args.installationNotes ?? null,
    tax_details: args.taxDetails ?? null,
  };
}

/** Rehydrate Assigned To / vendor select from saved installation_details. */
export function installationDetailsParticipantFromSaved(
  details: Record<string, unknown> | null | undefined,
  extras?: { sellerOrgId?: string | null; sellerName?: string | null }
): InstallationDetailsParticipantSelection | null {
  if (!details && !extras?.sellerOrgId) return null;
  const d = details || {};
  const membershipId = strPartyId(
    d.installer_membership_id ?? d.assignee_membership_id ?? d.installer_id ?? d.assignee_id
  );
  const organizationId = strPartyId(
    d.installer_org_id ?? d.assignee_org_id ?? extras?.sellerOrgId
  );
  const globalUserId = strPartyId(
    d.installer_global_user_id ?? d.assignee_global_user_id
  );
  const name = String(d.installer_name ?? extras?.sellerName ?? "").trim();
  const phone = String(d.contact_number ?? "").trim();
  if (!membershipId && !organizationId) return null;
  return {
    value: membershipId || organizationId,
    label: name || membershipId || organizationId,
    phone: phone || undefined,
    membershipId: membershipId || undefined,
    globalUserId: globalUserId || undefined,
    organizationId: organizationId || undefined,
  };
}
